import { List, ListBody, ListCell, ListHead, ListHeadCell, ListRow, ScrollArea } from '@plaidev/sour-react';
import type { ReactElement } from 'react';

import { columnDisplay, humanizeErrorMessage, rowsPreview } from './formatters';
import type { ErrorRow, GroupedError } from './types';

type ErrorSummarySectionProps = {
  grouped: GroupedError[];
  contractUk: string[] | undefined;
};

export function ErrorSummarySection({ grouped, contractUk }: ErrorSummarySectionProps): ReactElement {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-default">失敗理由サマリ</h2>
      {grouped.length === 0 ? (
        <p className="text-sm text-muted-light">失敗はありません。</p>
      ) : (
        <List>
          <ListHead>
            <ListRow>
              <ListHeadCell>列</ListHeadCell>
              <ListHeadCell>理由</ListHeadCell>
              <ListHeadCell width={80} align="right">
                件数
              </ListHeadCell>
              <ListHeadCell>該当行</ListHeadCell>
            </ListRow>
          </ListHead>
          <ListBody>
            {grouped.map((g) => (
              <ListRow key={`${g.column}-${g.message}`}>
                <ListCell>
                  <span className="text-sm text-default">{columnDisplay(g.column, contractUk)}</span>
                </ListCell>
                <ListCell>
                  <span className="text-sm text-default">{humanizeErrorMessage(g.message, contractUk)}</span>
                </ListCell>
                <ListCell width={80} align="right">
                  <span className="text-sm tabular-nums text-negative">{g.count}</span>
                </ListCell>
                <ListCell>
                  <span className="block max-w-0 min-w-full truncate text-xs text-muted-light">{rowsPreview(g.rows)}</span>
                </ListCell>
              </ListRow>
            ))}
          </ListBody>
        </List>
      )}
    </section>
  );
}

type ErrorDetailSectionProps = {
  errors: ErrorRow[];
  contractUk: string[] | undefined;
};

export function ErrorDetailSection({ errors, contractUk }: ErrorDetailSectionProps): ReactElement | null {
  if (errors.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-default">失敗明細（{errors.length}件）</h2>
      <ScrollArea className="max-h-[480px] w-full min-w-0" orientation="vertical">
        <List stickyTop={false}>
          <ListHead>
            <ListRow>
              <ListHeadCell width={96} align="right">
                行番号
              </ListHeadCell>
              <ListHeadCell width={220}>列</ListHeadCell>
              <ListHeadCell>理由</ListHeadCell>
            </ListRow>
          </ListHead>
          <ListBody>
            {errors.map((e, idx) => (
              <ListRow key={`${e.row}-${e.column}-${idx}`}>
                <ListCell width={96} align="right">
                  <span className="text-sm text-default">{e.row}</span>
                </ListCell>
                <ListCell width={220}>
                  <span className="block max-w-0 min-w-full truncate text-sm text-default">{columnDisplay(e.column, contractUk)}</span>
                </ListCell>
                <ListCell>
                  <span className="text-sm text-default">{humanizeErrorMessage(e.message, contractUk)}</span>
                </ListCell>
              </ListRow>
            ))}
          </ListBody>
        </List>
      </ScrollArea>
    </section>
  );
}
